import type { Circuit } from '../types/track.js';
import { cellCoordKey, getCorePath } from './circuitEdit.js';

export interface GridCoord {
  x: number;
  y: number;
}

const ORTHO_DELTAS = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
];

export function isWithinGrid(circuit: Circuit, x: number, y: number): boolean {
  return x >= 0 && x < circuit.width && y >= 0 && y < circuit.height;
}

export function listInfrastructureCells(circuit: Circuit): GridCoord[] {
  const cells: GridCoord[] = [];
  for (const cell of getCorePath(circuit.path)) {
    cells.push({ x: cell.x, y: cell.y });
  }
  for (const cell of circuit.jokerLap?.cells ?? []) {
    cells.push({ x: cell.x, y: cell.y });
  }
  for (const cell of circuit.startLane?.cells ?? []) {
    cells.push({ x: cell.x, y: cell.y });
  }
  return cells;
}

export function isInfrastructureCell(circuit: Circuit, x: number, y: number): boolean {
  return listInfrastructureCells(circuit).some((cell) => cell.x === x && cell.y === y);
}

export function decorCellKeys(circuit: Circuit): Set<string> {
  return new Set((circuit.decorCells ?? []).map((cell) => cellCoordKey(cell.x, cell.y)));
}

export function isDecorCell(circuit: Circuit, x: number, y: number): boolean {
  return decorCellKeys(circuit).has(cellCoordKey(x, y));
}

export function grandstandCellKeys(circuit: Circuit): Set<string> {
  const keys = new Set<string>();
  for (const stand of circuit.grandstands ?? []) {
    for (const cell of stand.cells) {
      keys.add(cellCoordKey(cell.x, cell.y));
    }
  }
  return keys;
}

export function isGrandstandCell(circuit: Circuit, x: number, y: number): boolean {
  return grandstandCellKeys(circuit).has(cellCoordKey(x, y));
}

export function signCellKeys(circuit: Circuit): Set<string> {
  const keys = new Set<string>();
  for (const sign of circuit.signs ?? []) {
    for (let dy = 0; dy < sign.height; dy++) {
      for (let dx = 0; dx < sign.width; dx++) {
        keys.add(cellCoordKey(sign.x + dx, sign.y + dy));
      }
    }
  }
  return keys;
}

export function isSignCell(circuit: Circuit, x: number, y: number): boolean {
  return signCellKeys(circuit).has(cellCoordKey(x, y));
}

export function vehicleCellKeys(circuit: Circuit): Set<string> {
  const keys = new Set<string>();
  for (const vehicle of circuit.vehicles ?? []) {
    keys.add(cellCoordKey(vehicle.x, vehicle.y));
  }
  return keys;
}

export function isVehicleCell(circuit: Circuit, x: number, y: number): boolean {
  return vehicleCellKeys(circuit).has(cellCoordKey(x, y));
}

export function decor3dCellKeys(circuit: Circuit): Set<string> {
  const keys = new Set<string>();
  for (const decor of circuit.decor3d ?? []) {
    for (const cell of decor.cells) {
      keys.add(cellCoordKey(cell.x, cell.y));
    }
  }
  return keys;
}

export function isDecor3dCell(circuit: Circuit, x: number, y: number): boolean {
  return decor3dCellKeys(circuit).has(cellCoordKey(x, y));
}

export function isPropCell(circuit: Circuit, x: number, y: number): boolean {
  return (
    isGrandstandCell(circuit, x, y) ||
    isSignCell(circuit, x, y) ||
    isVehicleCell(circuit, x, y) ||
    isDecor3dCell(circuit, x, y)
  );
}

function isAdjacentToPlacementZone(circuit: Circuit, x: number, y: number): boolean {
  for (const delta of ORTHO_DELTAS) {
    const nx = x + delta.x;
    const ny = y + delta.y;
    if (
      isInfrastructureCell(circuit, nx, ny) ||
      isDecorCell(circuit, nx, ny) ||
      isGrandstandCell(circuit, nx, ny) ||
      isSignCell(circuit, nx, ny) ||
      isVehicleCell(circuit, nx, ny) ||
      isDecor3dCell(circuit, nx, ny)
    ) {
      return true;
    }
  }
  return false;
}

/** Case libre pour placer décor, gradins ou panneau (hors tracé). */
export function canPlaceAmbientCell(circuit: Circuit, x: number, y: number): boolean {
  if (!isWithinGrid(circuit, x, y)) return false;
  if (isInfrastructureCell(circuit, x, y)) return false;
  if (isPropCell(circuit, x, y)) return true;
  if (isDecorCell(circuit, x, y)) return true;
  return isAdjacentToPlacementZone(circuit, x, y);
}

export function listAmbientPlaceableCells(circuit: Circuit): GridCoord[] {
  const keys = new Set<string>();
  const result: GridCoord[] = [];
  for (let y = 0; y < circuit.height; y++) {
    for (let x = 0; x < circuit.width; x++) {
      if (
        !canPlaceAmbientCell(circuit, x, y) ||
        isDecorCell(circuit, x, y) ||
        isPropCell(circuit, x, y)
      ) {
        continue;
      }
      const key = cellCoordKey(x, y);
      if (keys.has(key)) continue;
      keys.add(key);
      result.push({ x, y });
    }
  }
  return result;
}

/** Cases valides pour ancrer un rectangle de gradins ou panneau (damier inclus). */
export function listPropRectAnchorCells(circuit: Circuit): GridCoord[] {
  const keys = new Set<string>();
  const result: GridCoord[] = [];
  for (let y = 0; y < circuit.height; y++) {
    for (let x = 0; x < circuit.width; x++) {
      if (!canPlaceAmbientCell(circuit, x, y)) continue;
      if (
        isGrandstandCell(circuit, x, y) ||
        isSignCell(circuit, x, y) ||
        isVehicleCell(circuit, x, y) ||
        isDecor3dCell(circuit, x, y)
      ) {
        continue;
      }
      const key = cellCoordKey(x, y);
      if (keys.has(key)) continue;
      keys.add(key);
      result.push({ x, y });
    }
  }
  return result;
}

export function isPropRectAnchorCell(circuit: Circuit, x: number, y: number): boolean {
  return listPropRectAnchorCells(circuit).some((cell) => cell.x === x && cell.y === y);
}

export function listAllAmbientCoords(circuit: Circuit): GridCoord[] {
  const coords: GridCoord[] = [];
  for (const cell of circuit.decorCells ?? []) coords.push(cell);
  for (const stand of circuit.grandstands ?? []) {
    for (const cell of stand.cells) coords.push(cell);
  }
  for (const sign of circuit.signs ?? []) {
    for (let dy = 0; dy < sign.height; dy++) {
      for (let dx = 0; dx < sign.width; dx++) {
        coords.push({ x: sign.x + dx, y: sign.y + dy });
      }
    }
  }
  for (const vehicle of circuit.vehicles ?? []) {
    coords.push({ x: vehicle.x, y: vehicle.y });
  }
  for (const decor of circuit.decor3d ?? []) {
    for (const cell of decor.cells) coords.push(cell);
  }
  return coords;
}
